/**
 * Email Center → Link clicks (owner, 2026-09-30): every link people clicked
 * in our e-mails — our site, rndlist, LinkedIn, anything — most people first,
 * and which e-mail each click came from (admin_email_links.link_clicks).
 *
 * Nothing has to be done when writing an e-mail for a link to count: every
 * send has SendGrid click tracking on, and its webhook reports the original
 * link. The same link is one row however it was tagged (utm_* parameters,
 * "www." and a trailing slash are ignored).
 *
 * Also exports LinkRankTable, the per-campaign "Links clicked" table on a
 * campaign's funnel.
 *
 * 2026-10-15: invitation e-mails (Users → Bulk add / Send invites) are their
 * own kind, "Invites", and a bulk group narrows the clicks to its members.
 *
 * 2026-10-25: bot clicks (Backend/email_engagement.py — link scanners such as
 * Microsoft Defender and Gmail's link check, scripts, and any click within a
 * minute of delivery) are left out and counted apart; "Include automated"
 * counts them too, each marked "Bot click" with its reason.
 */
import React, { useMemo, useState } from 'react';
import { KPI, KPIRow, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet } from '../../kit';
import { Badge } from '../emailKit';
import { AutomatedToggle, EngagementNote } from '../dashboard/shared';
import { GroupNote, GroupSelect, useEmailGroups } from '../invites/shared';

export interface LinkRow {
  key: string; display: string; href: string; host: string; ours?: boolean;
  clicks: number; people: number; first_at?: string | null; last_at?: string | null;
  sources?: { kind: string; key: string; label: string; clicks: number }[];
  /** Of `clicks`, how many were bots (only when automated clicks are included). */
  bot_clicks?: number;
}
interface SourceRow { kind: string; key: string; label: string; clicks: number; people: number; links: number }

const PERIODS = ['7', '30', '90', '365', '0'];
const PERIOD_LABELS: Record<string, string> = {
  '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days', '365': 'Last year', '0': 'All time',
};
const KINDS = ['all', 'campaign', 'outreach', 'invite', 'automatic'];
const KIND_LABELS: Record<string, string> = {
  all: 'All e-mails', campaign: 'Campaigns', outreach: 'Researcher outreach', invite: 'Invites',
  automatic: 'Automatic e-mails',
};
const KIND_TONE: Record<string, string> = {
  campaign: 'bg-primary-100 text-primary-700 border-primary-200',
  outreach: 'bg-sky-50 text-sky-800 border-sky-200',
  invite: 'bg-amber-50 text-amber-800 border-amber-200',
  automatic: 'bg-grayscale-200 text-grayscale-1200 border-grayscale-300',
};

const KindTag: React.FC<{ kind: string; label: string; n?: number }> = ({ kind, label, n }) => (
  <span title={KIND_LABELS[kind] || kind}
    className={`inline-flex items-center gap-1 max-w-[12rem] rounded-full border px-2 py-0.5 text-[11px] ${KIND_TONE[kind] || KIND_TONE.automatic}`}>
    <span className="truncate">{label}</span>{n != null && <b className="shrink-0">{n}</b>}
  </span>
);

/** Ranked links: people, clicks, and (optionally) where the clicks came from.
 *  `onPick` makes a row open its detail underneath. */
export const LinkRankTable: React.FC<{
  rows: LinkRow[]; showSources?: boolean; picked?: string; onPick?: (key: string) => void;
  detail?: React.ReactNode; empty?: string;
}> = ({ rows, showSources, picked, onPick, detail, empty }) => {
  if (!rows.length) {
    return <div className="text-sm text-grayscale-1100 py-2">{empty || 'No link clicks yet.'}</div>;
  }
  const max = Math.max(1, ...rows.map(r => r.people));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-grayscale-1100 border-b border-grayscale-300">
            <th className="py-2 pr-2 w-8">#</th>
            <th className="py-2 pr-3">Link</th>
            <th className="py-2 pr-3 text-right" title="Different people who clicked it">People</th>
            <th className="py-2 pr-3 text-right" title="All clicks, repeats included">Clicks</th>
            <th className="py-2 pr-3 whitespace-nowrap">Last click</th>
            {showSources && <th className="py-2">From</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const open = picked === r.key;
            return (
              <React.Fragment key={r.key}>
                <tr onClick={onPick ? () => onPick(r.key) : undefined}
                  className={`border-b border-grayscale-200 align-top ${onPick ? 'cursor-pointer hover:bg-grayscale-200' : ''} ${open ? 'bg-primary-100' : ''}`}>
                  <td className="py-2 pr-2 text-grayscale-1100 tabular-nums">{i + 1}</td>
                  <td className="py-2 pr-3 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {r.ours && <span className="shrink-0 text-[10px] font-bold uppercase text-primary-og-600" title="Our own site">ours</span>}
                      <a href={r.href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        title={r.href} className="truncate max-w-[20rem] 2xl:max-w-[32rem] text-grayscale-1300 hover:underline">{r.display}</a>
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-grayscale-200 max-w-[20rem]" aria-hidden>
                      <div className="h-1 rounded-full bg-primary-400" style={{ width: `${Math.max(4, (100 * r.people) / max)}%` }} />
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right font-bold tabular-nums">{r.people.toLocaleString()}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">
                    {r.clicks.toLocaleString()}
                    {!!r.bot_clicks && <span className="block text-[10px] text-red-700" title="Clicks by link scanners">{r.bot_clicks} bot</span>}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap text-grayscale-1100">{r.last_at ? fmtDate(r.last_at) : '—'}</td>
                  {showSources && (
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        {(r.sources || []).slice(0, 2).map(s => <KindTag key={s.key} kind={s.kind} label={s.label} n={s.clicks} />)}
                        {(r.sources || []).length > 2 && (
                          <span className="text-[11px] text-grayscale-1100">+{(r.sources || []).length - 2} more</span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
                {open && detail && (
                  <tr><td colSpan={showSources ? 6 : 5} className="p-0">{detail}</td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/** Who clicked one link, when, and from which e-mail. */
const ClickDetail: React.FC<{
  linkKey: string; days: string; source: string; sourceKey: string; group: string; automated: boolean;
}> = ({ linkKey, days, source, sourceKey, group, automated }) => {
  const q = useAdminGet('email', 'link_clicks_detail',
    { key: linkKey, days, source, source_key: sourceKey, ...(group ? { group } : {}), ...(automated ? { automated: '1' } : {}) },
    [linkKey, days, source, sourceKey, group, automated]);
  const rows: any[] = q.data?.clicks || [];
  return (
    <div className="bg-grayscale-200 border-y border-grayscale-300 px-4 py-3">
      {q.error && <Msg kind="error">{q.error}</Msg>}
      {q.loading && !q.data ? <Loading /> : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-grayscale-1100">
              <th className="pb-1 pr-3">When</th><th className="pb-1 pr-3">Who</th><th className="pb-1">From</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-grayscale-200">
                <td className="py-1.5 pr-3 whitespace-nowrap text-grayscale-1100">{fmtDate(r.at)}</td>
                <td className="py-1.5 pr-3">
                  {r.name ? <><b>{r.name}</b> <span className="text-grayscale-1100">{r.email}</span></> : r.email || '—'}
                  {r.bot && <span className="ml-1.5 cursor-help" title={r.bot_reason || 'A link scanner, not the person'}><Badge tone="red">Bot click</Badge></span>}
                </td>
                <td className="py-1.5"><KindTag kind={r.kind} label={r.source} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const LinkClicks: React.FC = () => {
  const [days, setDays] = useState('30');
  const [source, setSource] = useState('all');
  const [q, setQ] = useState('');
  const [email, setEmail] = useState('');          // one e-mail (source key) picked in "By e-mail"
  const [picked, setPicked] = useState('');
  const [group, setGroup] = useState('');          // a bulk group: only its members' clicks
  const [automated, setAutomated] = useState(false); // bot clicks counted too
  const groups = useEmailGroups();
  // One e-mail picked: the server counts that e-mail's clicks only, so the
  // numbers and the order are its own (sources still lists every e-mail).
  const res = useAdminGet('email', 'link_clicks',
    { days, source, source_key: email, ...(group ? { group } : {}), ...(automated ? { automated: '1' } : {}) },
    [days, source, email, group, automated]);
  const d: any = res.data || {};
  const sources: SourceRow[] = d.sources || [];
  const rows: LinkRow[] = useMemo(() => {
    const all: LinkRow[] = d.links || [];
    const needle = q.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
    return all.filter(r => !needle || r.key.toLowerCase().includes(needle) || r.href.toLowerCase().includes(needle));
  }, [d.links, q]);
  const t = d.totals || {};

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-grayscale-1100 -mb-1">
        Every link clicked in an e-mail we sent — campaigns, researcher outreach and the automatic mails.
        Any link in an e-mail is tracked (SendGrid click tracking is on for every send); the same link
        counts as one row however it was tagged.
      </p>
      <div className="flex gap-2 flex-wrap items-center">
        <Select value={days} onChange={v => { setDays(v); setPicked(''); }} options={PERIODS} labels={PERIOD_LABELS} />
        <Select value={source} onChange={v => { setSource(v); setEmail(''); setPicked(''); }} options={KINDS} labels={KIND_LABELS} />
        {(groups.groups.length > 0 || group) && (
          <GroupSelect value={group} onChange={v => { setGroup(v); setEmail(''); setPicked(''); }}
            groups={groups.groups} all="Everyone" />
        )}
        <TextInput value={q} onChange={setQ} placeholder="Filter links, e.g. rndlist or /tech/" wide />
        <AutomatedToggle value={automated} onChange={v => { setAutomated(v); setPicked(''); }} />
      </div>
      {group && (
        <GroupNote group={groups.byId(group)} id={group} fallbackLabel={String(d.filters?.group?.label || '')}
          people={d.filters?.group?.people} what="clicks by">
          {' '}<button type="button" onClick={() => { setGroup(''); setPicked(''); }}
            className="underline underline-offset-2 text-primary-og-600">Show everyone</button>
        </GroupNote>
      )}
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading /> : (
        <>
          <KPIRow>
            <KPI label="Clicks" value={(t.clicks ?? 0).toLocaleString()}
              hint={email ? `${PERIOD_LABELS[days]} · one e-mail` : PERIOD_LABELS[days]} />
            <KPI label="People who clicked" value={(t.people ?? 0).toLocaleString()} />
            <KPI label="Different links" value={(t.links ?? 0).toLocaleString()} />
            {t.bot_clicks !== undefined && (
              <KPI label="Bot clicks" value={Number(t.bot_clicks || 0).toLocaleString()}
                hint={automated ? 'included above' : 'left out above: link scanners, not people'} />
            )}
          </KPIRow>
          <EngagementNote eng={d.engagement} automated={automated} />
          <Section title="Links, most people first"
            right={email ? (
              <button type="button" onClick={() => setEmail('')} className="text-xs underline text-primary-og-600">
                Only from “{sources.find(s => s.key === email)?.label || 'one e-mail'}” — show all
              </button>
            ) : undefined}>
            <LinkRankTable rows={rows} showSources picked={picked}
              onPick={k => setPicked(p => (p === k ? '' : k))}
              detail={picked ? <ClickDetail linkKey={picked} days={days} source={source} sourceKey={email} group={group}
                automated={automated} /> : null}
              empty={q || email ? 'No clicked link matches.' : 'No link was clicked in this period.'} />
            {d.truncated && <div className="text-[11px] text-amber-800">Showing the newest clicks only; narrow the period.</div>}
          </Section>
          {sources.length > 0 && (
            <Section title="By e-mail">
              <div className="flex flex-col divide-y divide-grayscale-200">
                {sources.map(s => (
                  <button key={s.key} type="button" onClick={() => { setEmail(e => (e === s.key ? '' : s.key)); setPicked(''); }}
                    className={`flex items-center justify-between gap-3 py-2 text-left hover:bg-grayscale-200 px-1 ${email === s.key ? 'bg-primary-100' : ''}`}>
                    <KindTag kind={s.kind} label={s.label} />
                    <span className="text-xs text-grayscale-1100 whitespace-nowrap">
                      <b className="text-grayscale-1300">{s.people}</b> people · {s.clicks} clicks · {s.links} link{s.links === 1 ? '' : 's'}
                    </span>
                  </button>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
};

export default LinkClicks;
